import { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { 
    OfferMerchantAdmin, 
    FindAllOffersAdminQuery, 
    FindAllOffersAdminQueryVariables 
} from "../../../../../schemaTypes";


export const findAllOffersAdminQuery = gql`
  query FindAllOffersAdmin(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllOffersAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        title
        offerType
        redemptions
        startDate
        endDate
        status
        createdAt
        createdBy {
          admin {
            id
            merchantProfile {
              id
              companyName
            }
          }
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllOffersAdmin {
 data: OfferMerchantAdmin[]
 total: number;
 page: number;
 pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreOffers: boolean;
}

interface Props {
  variables: FindAllOffersAdminQueryVariables;
  children: (data: WithFindAllOffersAdmin) => React.ReactElement | null;
}

export const FindAllOffersAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllOffersAdminQuery,
    FindAllOffersAdminQueryVariables
  >(findAllOffersAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllOffersAdminData = data?.findAllOffersAdmin.data || [];
  const page = data?.findAllOffersAdmin?.page || variables.page;
  const pageSize = data?.findAllOffersAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllOffersAdmin?.total || 0;
  const totalPages = data?.findAllOffersAdmin?.totalPages || 0;
  const hasMoreOffers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreOffers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllOffersAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllOffersAdmin: {
            ...fetchMoreResult.findAllOffersAdmin,
            data: [
              ...(prev?.findAllOffersAdmin?.data || []),
              ...(fetchMoreResult.findAllOffersAdmin.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreOffers]);

  return children({
    data: findAllOffersAdminData as OfferMerchantAdmin[],
    loading,
    loadMore,
    hasMoreOffers,
    page,
    pageSize,
    total,
    totalPages,
  });
};